import React, { useState, useEffect } from "react";
import Select from "react-select";
import { getBranchOwner, get, post, api } from "@config";
import CNAES_ITEM from './../utils/cnae_item.json'
import ITEM_CODE from './../utils/item_code_services.json'

import { useNfse } from "../context";
import { StyledCard, StyledCardHeader, SectionTitle, StyledInputGroup, LayoutGrid, HelperButton } from './styles';
import ServiceFinder from '../../components/ServiceFinder';
import { getExigibilidadeFromNatOp, EXIGIBILIDADE_LABELS } from '../utils/natOpToExigibilidade';
import CityAutocomplete from './CityAutocomplete';

const normalizeItemLawKey = (value) => {
  if (!value && value !== 0) return "";
  let str = String(value).trim();
  const digits = str.replace(/\D/g, "");
  if (!str.includes(".") && digits.length >= 3) {
    if (digits.length === 3) {
      str = `${parseInt(digits[0], 10)}.${digits.slice(1)}`;
    } else if (digits.length >= 4) {
      const major = parseInt(digits.slice(0, 2), 10);
      str = `${major}.${digits.slice(2, 4)}`;
    }
  }
  if (str.startsWith("0") && str.includes(".")) {
    str = str.replace(/^0+/, "");
  }
  return str;
};

const getServiceInfoFromItemLaw = (value) => {
  if (!value && value !== 0) return null;
  const key = normalizeItemLawKey(value);
  if (ITEM_CODE[key] && ITEM_CODE[key].length > 0) {
    return ITEM_CODE[key][0];
  }
  const directKey = String(value).trim();
  if (ITEM_CODE[directKey] && ITEM_CODE[directKey].length > 0) {
    return ITEM_CODE[directKey][0];
  }
  return null;
};

export default function Service(props) {
  const { service, setService, rps } = useNfse();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [natOpList, setNatOpList] = useState([]);
  const [showFinder, setShowFinder] = useState(false);

  const getSecondaryCnae = () => {
    setLoading(true);
    const branch = getBranchOwner();
    const url = `${api.branches}${branch.id}/get-secondary-cnae/`;
    get(url, (resData) => {
      for (let x = 0; x < resData.length; x++) {
        resData[x].label = `${resData[x].value} | ${resData[x].label}`;
      }
      setLoading(false);
      setData(resData);
    }, () => setLoading(false));
  };

  const loadActivities = () => {
    setLoading(true);
    const branch = getBranchOwner();
    const url = `${api.branches}${branch.id}/load-activities/`;
    get(url, (response) => {
      setLoading(false);
      if (response.status === true) {
        getSecondaryCnae();
      }
    }, () => setLoading(false));
  };

  const changeField = (e) => {
    const { name: key, value } = e.target;
    if (key === "aliq_iss") {
      if (parseFloat(value) > 100 || parseFloat(value) < 0) {
        return;
      }
    }
    setService(prev => {
      const nextService = { ...prev, [key]: value };
      if (key === "item_list_service") {
        const serviceInfo = getServiceInfoFromItemLaw(value);
        if (serviceInfo) {
          nextService.service_code = serviceInfo.code;
          if (!nextService.aliq_iss || nextService.aliq_iss === "0") {
            nextService.aliq_iss = serviceInfo.aliq;
          }
        }
      }
      return nextService;
    });
  };

  const selectActivity = (cnae) => {
    if (!cnae) return;
    
    const cnaeValue = cnae.value;
    const cnae_item = CNAES_ITEM[cnaeValue];
    let newService = { ...service, cnae_code: cnaeValue };

    if (cnae_item) {
      const item_law = cnae_item.service_code;
      // Sempre atualiza o item da lista se encontrado o mapeamento
      if (item_law) {
        newService.item_list_service = item_law;
        
        const serviceInfo = getServiceInfoFromItemLaw(item_law);
        if (serviceInfo) {
          newService.service_code = serviceInfo.code;
          if (!newService.aliq_iss || newService.aliq_iss === "0") {
            newService.aliq_iss = serviceInfo.aliq;
          }
        } else {
          // Fallback seguro usando apenas os dígitos numéricos sem zeros extras
          const digits = item_law.replace(/\D/g, "");
          if (digits && !newService.service_code) {
            newService.service_code = digits;
          }
        }
      }
    } else {
      // Caso não encontre no mapeamento local do frontend, preservamos
      // o que já está lá (útil para replicação de notas customizadas)
    }

    setService(newService);
  };

  const selectNatOp = (nat) => {
    const branch = getBranchOwner();
    // Usa city_code da API (mapeamento real da prefeitura) ao invés do mapeamento estático
    const exigibilidade = nat.city_code || getExigibilidadeFromNatOp(nat.value);
    setService(prev => ({ 
      ...prev, 
      nat_op: nat,
      exigibilidade_iss: exigibilidade,
      city_code_provider: String(nat.value) === "2" ? prev.city_code_provider : ((branch.address && branch.address.city_code) || "")
    }));
  };

  const getNatOp = () => {
    setLoading(true);
    const branch = getBranchOwner();
    const url = `${api.nfse.config}get-nat-op/?branch_owner=${branch.id}`;
    get(url, (natList) => {
      let temp = natList.map(i => ({label: i.description, value: i.code, city_code: i.city_code}));
      setLoading(false);
      setNatOpList(temp);
    }, () => setLoading(false));
  };
  
  const loadNatOp = () => {
    setLoading(true);
    const branch = getBranchOwner();
    const url = `${api.nfse.config}load-nat-op/?branch_owner=${branch.id}`;
    post(url, {}, () => getNatOp(), () => setLoading(false));
  };

  useEffect(() => {
    getSecondaryCnae();
    getNatOp();

    const branch = getBranchOwner();
    const defaultCityCode = (branch.address && branch.address.city_code) || "";
    setService(prev => ({
      ...prev,
      exigibilidade_iss: prev.exigibilidade_iss || "1",
      city_code_provider: prev.city_code_provider || defaultCityCode
    }));
    // Nota: exigibilidade_iss será sobrescrita automaticamente ao selecionar natOp
  }, []);

  return (
    <div className='mb-4'>
      {loading ? (
        <Loading />
      ) : (
        <StyledCard>
          <StyledCardHeader>
            <SectionTitle><i className="fas fa-file-invoice"></i> Dados do Serviço e RPS</SectionTitle>
          </StyledCardHeader>

          <LayoutGrid style={{ marginBottom: '24px' }}>
            <div>
              <StyledInputGroup>
                <label>Natureza da Operação *</label>
                <Select
                  value={(service && service.nat_op) ? service.nat_op : (natOpList.find(n => String(n.value) === String(rps && rps.rps_nat_op)) || null)}
                  onChange={selectNatOp}
                  options={natOpList}
                  placeholder="Selecione aqui"
                  noOptionsMessage={() => "Por favor, clique no botão abaixo para importar as naturezas."}
                />
              </StyledInputGroup>
              {!natOpList.length ? (
                <HelperButton type="button" onClick={loadNatOp}>
                  <i className="fas fa-download"></i> Importar Naturezas
                </HelperButton>
              ) : null}
            </div>
            <div>
              <StyledInputGroup>
                <label>Atividade (CNAE) *</label>
                <Select
                  value={data.find(d => {
                    const dVal = String(d.value || "").replace(/\D/g, "");
                    const sVal = String((service && service.cnae_code) || "").replace(/\D/g, "");
                    return dVal === sVal && dVal !== "";
                  }) || null}
                  onChange={selectActivity}
                  options={data}
                  placeholder="Selecione aqui"
                  noOptionsMessage={() => "Por favor, clique no botão abaixo para importar as atividades."}
                />
              </StyledInputGroup>
              {!data.length ? (
                <HelperButton type="button" onClick={loadActivities}>
                  <i className="fas fa-sync"></i> Importar Atividades
                </HelperButton>
              ) : null}
            </div>
          </LayoutGrid>

          <LayoutGrid style={{ marginBottom: '24px' }}>
            <div>
              <StyledInputGroup>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <label>Item Lei *</label>
                  <button 
                    className="btn btn-link btn-sm p-0 mb-1" 
                    style={{ textDecoration: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                    onClick={() => setShowFinder(true)}
                  >
                    <i className="fas fa-search me-1"></i> Buscar Item/Código
                  </button>
                </div>
                <input
                  name="item_list_service"
                  onChange={changeField}
                  value={(service && service.item_list_service) || ""}
                />
                <a href="https://wwwx.gissonline.com.br/Rel/rol_atividades.cfm?pid=1199" target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#0088cc', marginTop: '4px' }}>
                  <i className="fas fa-external-link-alt" style={{marginRight: '4px'}}></i> Consultar Itens da Lei (GissOnline)
                </a>
              </StyledInputGroup>
            </div>
            <div>
              <StyledInputGroup>
                <label>Código do Serviço *</label>
                <input
                  name="service_code"
                  onChange={changeField}
                  value={(service && service.service_code) || ""}
                />
                <a href="https://www.planalto.gov.br/ccivil_03/leis/lcp/lcp116.htm" target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#0088cc', marginTop: '4px' }}>
                  <i className="fas fa-external-link-alt" style={{marginRight: '4px'}}></i> Consultar Lista de Serviços (LC 116/2003)
                </a>
              </StyledInputGroup>
            </div>
          </LayoutGrid>

          <LayoutGrid style={{ marginBottom: '24px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <StyledInputGroup>
                <label>Código NBS (Reforma Tributária - IBS/CBS)</label>
                <input
                  name="nbs_code"
                  onChange={changeField}
                  value={(service && service.nbs_code) || ""}
                  placeholder="Ex: 101010100 (Apenas números, deixe em branco para Padrão)"
                  maxLength="9"
                />
                <small style={{ fontSize: '0.75rem', color: '#667085', marginTop: '4px' }}>
                  Preencha os 9 dígitos do NBS apenas se o seu contador orientar a declaração de IBS/CBS. Caso fique vazio, o sistema emitirá a nota dispensando o NBS.
                </small>
              </StyledInputGroup>
            </div>
          </LayoutGrid>

          <LayoutGrid style={{ marginBottom: '24px' }}>
            <div style={{ gridColumn: service && service.nat_op && String(service.nat_op.value) === "2" ? 'auto' : '1 / -1' }}>
              <StyledInputGroup>
                <label>Exigibilidade do ISS</label>
                <Select
                  value={(() => {
                    const code = (service && service.exigibilidade_iss) || "1";
                    return { value: code, label: `${code} - ${EXIGIBILIDADE_LABELS[code] || "Exigível (Regra Geral)"}` };
                  })()}
                  onChange={(opt) => setService(prev => ({ ...prev, exigibilidade_iss: opt.value }))}
                  options={Object.entries(EXIGIBILIDADE_LABELS).map(([k, v]) => ({ value: k, label: `${k} - ${v}` }))}
                  placeholder="Selecione..."
                />
                <small style={{ fontSize: '0.75rem', color: '#667085', marginTop: '4px' }}>
                  Preenchido automaticamente pela Natureza da Operação. Altere se necessário.
                </small>
              </StyledInputGroup>
            </div>

            {(service && service.nat_op && String(service.nat_op.value) === "2") && (
              <div>
                <StyledInputGroup>
                  <label>Município da Prestação do Serviço *</label>
                  <CityAutocomplete
                    name="city_code_provider"
                    onChange={changeField}
                    value={(service && service.city_code_provider) || ""}
                    placeholder="Ex: São Paulo"
                  />
                  <small style={{ fontSize: '0.75rem', color: '#667085', marginTop: '4px' }}>
                    Pesquise a cidade onde o imposto será recolhido.
                  </small>
                </StyledInputGroup>
              </div>
            )}
          </LayoutGrid>

          <StyledCardHeader style={{ marginTop: '8px' }}>
             <SectionTitle><i className="fas fa-tags"></i> Discriminação e Valores</SectionTitle>
          </StyledCardHeader>

          <div style={{ marginBottom: '24px' }}>
            <StyledInputGroup>
              <label>Descrição da Nota (Discriminação do Serviço) *</label>
              <textarea
                name="description"
                rows={4}
                onChange={changeField}
                value={(service && service.description) || ""}
                placeholder="Detalhe o serviço prestado..."
              />
            </StyledInputGroup>
          </div>

          <LayoutGrid style={{ marginBottom: '8px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <div>
              <StyledInputGroup>
                <label>Alíquota ISS (%)</label>
                <input
                  onChange={changeField}
                  name="aliq_iss"
                  type="number"
                  value={(service && service.aliq_iss) || ""}
                />
              </StyledInputGroup>
            </div>
            {!props.noTotal ? (
              <div>
                <StyledInputGroup>
                  <label>Valor do Serviço (R$)</label>
                  <input
                    onChange={changeField}
                    name="value"
                    type='number'
                    value={(service && service.value) !== undefined ? service.value : ""}
                  />
                </StyledInputGroup>
              </div>
            ) : null}
            <div>
              <StyledInputGroup>
                <label>ISS Retido?</label>
                <select
                  name="iss_retained"
                  onChange={changeField}
                  value={(service && service.iss_retained) || "2"}
                >
                  <option value="1">Sim</option>
                  <option value="2">Não</option>
                </select>
              </StyledInputGroup>
            </div>
            {(service && service.iss_retained === "1") ? (
              <div>
                <StyledInputGroup>
                  <label>Valor ISS Retido (R$)</label>
                  <input
                    onChange={changeField}
                    name="iss_retained_value"
                    type='number'
                    value={(service && service.iss_retained_value) || ""}
                  />
                </StyledInputGroup>
              </div>
            ) : null}
          </LayoutGrid>



        </StyledCard>
      )}
      <ServiceFinder 
        show={showFinder} 
        onHide={() => setShowFinder(false)} 
        onSelect={(selected) => {
          setService(prev => ({
            ...prev,
            item_list_service: selected.item_list_service,
            service_code: selected.service_code,
            aliq_iss: selected.aliq_iss || prev.aliq_iss
          }));
        }} 
      />
    </div>
  );
}

function Loading(props) {
  return (
    <p className="text-center py-4" style={{fontSize: '1.2rem', color: '#667085'}}>
      Carregando informações fiscais <i className="fas fa-circle-notch fa-spin ms-2"></i>
    </p>
  );
}



// WEBPACK FOOTER //
// ./src/views/Fiscal/NFSe/emit/components/service.jsx